/**
 * ManageDetailsPage
 *
 * Full-page mobile view for managing group details.
 * Replaces the Manage Details dialog on mobile.
 */

import React, { ReactElement, useState, useEffect } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Stack,
  CircularProgress,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { GroupDetailsContent } from "./components/GroupDetailsContent";
import { useCategoryGroups } from "./hooks/useCategoryGroups";
import { useCategories } from "./hooks/useCategories";
import { useCategoryTransactions } from "./hooks/useCategoryTransactions";
import { useCategoryHelpers } from "./hooks/useCategoryHelpers";
import type { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { api } from "../../services/api";
import { TransactionCategoryDetail } from "../../types";

interface ManageDetailsPageProps {
  workspaceId: string;
}

function ManageDetailsPage({ workspaceId }: ManageDetailsPageProps): ReactElement {
  const navigate = useNavigate();
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
  const location = useLocation();
  const pathParts = location.pathname.split("/").filter(Boolean);
  const settingsIndex = pathParts.indexOf("settings");
  const typeParam = pathParts[settingsIndex + 2] as "expense" | "income" | undefined;
  const groupId = pathParts[settingsIndex + 3];

  const type = typeParam === "income" ? "income" : "expense";

  const { groups, setGroups } = useCategoryGroups({ workspaceId, type });
  const { categories, setCategories } = useCategories({ workspaceId, type });
  const { transactions } = useCategoryTransactions({ workspaceId, type });

  const [editingGroupDetails, setEditingGroupDetails] = useState<TransactionCategoryDetail[]>([]);
  const [originalGroupDetails, setOriginalGroupDetails] = useState<TransactionCategoryDetail[]>([]);
  const [newDetailName, setNewDetailName] = useState("");
  const [newDetailType, setNewDetailType] = useState<"list" | "measurable" | "">("");
  const [newDetailUnit, setNewDetailUnit] = useState("");
  const [newListItems, setNewListItems] = useState<string[]>([]);
  const [editingDetailIndex, setEditingDetailIndex] = useState<number | null>(null);
  const [editingOriginalDetail, setEditingOriginalDetail] = useState<TransactionCategoryDetail | null>(null);
  const [detailNameError, setDetailNameError] = useState("");
  const [savingDetails, setSavingDetails] = useState(false);
  const [error, setError] = useState("");

  const {
    isGroupDetailUsed,
    hasGroupDetailsChanged,
    hasPartialFormData,
    hasEditedDetailChanged,
  } = useCategoryHelpers({
    categories,
    groups,
    transactions,
  });

  const group = groups.find((g) => g.id === groupId);
  const groupName = group?.name ?? "";

  useEffect(() => {
    if (group?.details) {
      setEditingGroupDetails([...group.details]);
      setOriginalGroupDetails([...group.details]);
    } else if (group && (!group.details || group.details.length === 0)) {
      setEditingGroupDetails([]);
      setOriginalGroupDetails([]);
    }
  }, [group?.id, group?.details?.length]);

  const handleSave = async (): Promise<void> => {
    if (!group || !group.id || !workspaceSlug) return;
    setSavingDetails(true);
    setError("");

    try {
      await api.updateGroup(workspaceId, group.id, undefined, undefined, editingGroupDetails);
      await new Promise((r) => setTimeout(r, 200));
      const [groupsData, categoriesData] = await Promise.all([
        api.getGroups(workspaceId, type),
        api.getCategories(workspaceId, type),
      ]);
      setGroups(groupsData);
      setCategories(categoriesData);
      navigate(`/${workspaceSlug}/settings/categories`, { replace: true });
    } catch (err: any) {
      setError(err.message || "Failed to save group details");
    } finally {
      setSavingDetails(false);
    }
  };

  const handleBack = (): void => {
    navigate(`/${workspaceSlug}/settings/categories`, { replace: true });
  };

  const handleGroupDetailDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = parseInt(String(active.id), 10);
    const newIndex = parseInt(String(over.id), 10);
    if (isNaN(oldIndex) || isNaN(newIndex)) return;
    const reordered = arrayMove(editingGroupDetails, oldIndex, newIndex);
    setEditingGroupDetails(reordered);
    if (editingDetailIndex !== null) {
      if (editingDetailIndex === oldIndex) {
        setEditingDetailIndex(newIndex);
      } else if (editingDetailIndex > oldIndex && editingDetailIndex <= newIndex) {
        setEditingDetailIndex(editingDetailIndex - 1);
      } else if (editingDetailIndex < oldIndex && editingDetailIndex >= newIndex) {
        setEditingDetailIndex(editingDetailIndex + 1);
      }
    }
  };

  const handleEditGroupDetail = (index: number): void => {
    const detail = editingGroupDetails[index];
    setEditingDetailIndex(index);
    setEditingOriginalDetail(detail);
    setNewDetailName(detail.name);
    setNewDetailType(detail.type);
    setNewDetailUnit(detail.unit || "");
    setNewListItems([...(detail.listItems || [])]);
    setDetailNameError("");
  };

  const handleRemoveGroupDetail = (index: number): void => {
    setEditingGroupDetails(editingGroupDetails.filter((_, i) => i !== index));
    if (editingDetailIndex === index) {
      setEditingDetailIndex(null);
      setEditingOriginalDetail(null);
      setNewDetailName("");
      setNewDetailType("");
      setNewDetailUnit("");
      setNewListItems([]);
      setDetailNameError("");
    } else if (editingDetailIndex !== null && editingDetailIndex > index) {
      setEditingDetailIndex(editingDetailIndex - 1);
    }
  };

  const canSave =
    !savingDetails &&
    hasGroupDetailsChanged(editingGroupDetails, originalGroupDetails) &&
    editingDetailIndex === null &&
    !hasPartialFormData(newDetailName, newDetailType, newDetailUnit, newListItems, editingDetailIndex);

  if (!group) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0,
          position: "sticky",
          top: 0,
          zIndex: 10,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <IconButton
          component={Link}
          to={`/${workspaceSlug}/settings/categories`}
          size="medium"
          sx={{ minWidth: 48, minHeight: 48 }}
          aria-label="Back to Categories"
        >
          <ArrowBackIcon sx={{ fontSize: 24 }} />
        </IconButton>
        <Typography variant="h6" sx={{ flex: 1, pr: 1, fontSize: "1.125rem" }}>
          Manage Details - {groupName}
        </Typography>
      </Paper>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
          p: 2,
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        <GroupDetailsContent
          groupName={groupName}
          editingGroupDetails={editingGroupDetails}
          setEditingGroupDetails={setEditingGroupDetails}
          newDetailName={newDetailName}
          setNewDetailName={setNewDetailName}
          newDetailType={newDetailType}
          setNewDetailType={setNewDetailType}
          newDetailUnit={newDetailUnit}
          setNewDetailUnit={setNewDetailUnit}
          newListItems={newListItems}
          setNewListItems={setNewListItems}
          detailNameError={detailNameError}
          setDetailNameError={setDetailNameError}
          editingDetailIndex={editingDetailIndex}
          setEditingDetailIndex={setEditingDetailIndex}
          editingOriginalDetail={editingOriginalDetail}
          setEditingOriginalDetail={setEditingOriginalDetail}
          savingDetails={savingDetails}
          categories={categories}
          groups={groups}
          transactions={transactions}
          isGroupDetailUsed={isGroupDetailUsed}
          hasPartialFormData={hasPartialFormData}
          hasEditedDetailChanged={hasEditedDetailChanged}
          onDragEnd={handleGroupDetailDragEnd}
          onEditDetail={handleEditGroupDetail}
          onRemoveDetail={handleRemoveGroupDetail}
        />
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={savingDetails}
            sx={{ flex: 1, py: 1.5 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!canSave}
            sx={{ flex: 1, py: 1.5 }}
          >
            {savingDetails ? "Saving..." : "Save"}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}

export default ManageDetailsPage;
